import { apiClient } from '@/api/client';
import type { FeaturedEvent, PublicEvent, PublicEventDetail } from '../types';

/**
 * Member-facing event reads (MYN-97 / MYN-98).
 *
 * Both endpoints are resolved server-side against a single clock: the featured
 * event is the earliest upcoming queued, published, non-private event, and the
 * public section is that same predicate plus "public access type and
 * show-on-public", already capped and already ordered. Neither response is
 * sorted, filtered, re-ordered, or re-picked here - promotion happens because
 * the next request resolves differently, with no client state and no write.
 *
 * `/v1` is supplied by `API_BASE_URL`, so the paths below are version-free.
 */

/**
 * The event currently displayed as featured, or `null` when the queue is empty
 * or every entry is in the past.
 *
 * @returns the featured event, or `null` when nothing is eligible.
 */
export async function fetchFeaturedEvent(): Promise<FeaturedEvent | null> {
  const res = await apiClient.get<FeaturedEvent | null>('/events/featured');
  // A 200 with an empty body deserialises to `''` through axios, which is
  // falsy but not `null` - normalise so callers only ever branch on `null`.
  return res.data || null;
}

/**
 * The curated public events section for Home and Benefits, capped by the
 * backend's own configured limit.
 *
 * @returns the eligible public events in the order the API returned them, or an
 *   empty array when none are eligible.
 */
export async function fetchPublicSectionEvents(): Promise<PublicEvent[]> {
  const res = await apiClient.get<PublicEvent[]>('/events/public-section');
  // Defends the `.map()` in `PublicEventsSection` against a null/absent body,
  // which would otherwise surface as a render crash rather than an empty
  // section.
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * One event by id (MYN-93), behind the `/e/:id` private-event link route.
 * `@Public()` on the backend - reachable with or without a session, and
 * reachability is decided by `isPrivate`, never by access type or auth state.
 *
 * Rejects (propagates, never swallows) on a 404 - an archived, cancelled, or
 * soft-deleted event - so `PrivateEventRoute` renders its not-available state
 * rather than a partial page.
 */
export async function fetchPublicEvent(id: string): Promise<PublicEventDetail> {
  const res = await apiClient.get<PublicEventDetail>(`/events/${id}`);
  return res.data;
}
